// Backend ↔ design mapping for the AI Agents section (design: state/agents.tsx).
// The design's mock store is replaced by the real APIs the old pages call:
//   agents     GET/POST/PUT/DELETE /api/ai-agent/config, POST /api/ai-agent/toggle, ...
//   analytics  GET /api/ai-agent/analytics?period=7d[&facility_id=] (items 216–218)
//   templates  GET /api/ai-agent/templates, GET /api/ai-agent/templates/:id/sample (items 219–220)
//   knowledge  /api/kb/*
import axios from "axios";
import config from "../../../config";
import { organizationsAPI } from "../../../services/apiService";

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

/** Same host the old embed-code sections use for the widget script. */
export const WIDGET_BASE_URL = process.env.REACT_APP_WIDGET_URL || "https://api.getolliai.com";

/** Widget assets come back as server paths; the old configurator prefixes the API host. */
const API_HOST = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, "") : window.location.origin;
export const resolveUrl = (url) => {
  if (!url) return "";
  if (/^(https?:|blob:|data:)/.test(url)) return url;
  return API_HOST + url;
};

/* ---------- Channels ---------- */

export const CHANNEL_LABEL = { web_widget: "Web widget", web_voice: "Web voice", voice_call: "Voice call", whatsapp: "WhatsApp" };
export const CHANNEL_KEY = Object.fromEntries(Object.entries(CHANNEL_LABEL).map(([k, v]) => [v, k]));

export const parseJson = (raw, fallback) => {
  if (raw == null || raw === "") return fallback;
  if (typeof raw !== "string") return raw;
  try { return JSON.parse(raw); } catch (_) { return fallback; }
};

/** Channel keys a template enables (channel_config: { web_widget: { enabled }, ... }). */
export const enabledChannels = (channelConfig) => Object.entries(parseJson(channelConfig, {}) || {}).filter(([, v]) => v && v.enabled).map(([k]) => k);
export const channelLabels = (channelConfig) => enabledChannels(channelConfig).map((k) => CHANNEL_LABEL[k] || k);

/** A single-facility template has nothing to route between (old page: templateIsMultiFacility). */
export const templateIsMultiFacility = (template) => {
  if (!template) return false;
  const t = String(template.template_type || "").toLowerCase();
  return !!t && t !== "single" && !t.includes("single");
};

/* ---------- Template catalog fields (item 219) ---------- */

/** "front_desk" → "Front desk". */
export const caseLabel = (key) => {
  if (!key) return "";
  const s = String(key).replace(/[_-]+/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const SHORT_LANG = { en: "English", hi: "Hindi" };
/** A template's languages ("hi", "en-IN", …) as names. */
export const templateLanguages = (template) => {
  const codes = parseJson(template?.languages, []);
  if (!Array.isArray(codes)) return [];
  return codes.map((code) => {
    const c = String(code);
    const exact = LANGS.find(([k]) => k.toLowerCase() === c.toLowerCase());
    if (exact) return exact[1];
    const base = c.split("-")[0].toLowerCase();
    return SHORT_LANG[base] || (LANGS.find(([k]) => k.split("-")[0] === base) || [c, c])[1];
  });
};

/**
 * GET /api/ai-agent/templates/:id/sample (item 220) → { turns: [{ role: "patient" | "agent", text }], sample }.
 * sample=false means the template has no written conversation yet: turns is just its greeting.
 * Resolves to null when the request fails.
 */
export async function loadTemplateSample(templateId) {
  if (!templateId) return null;
  try {
    const r = await axios.get(`${config.API_BASE_URL}/api/ai-agent/templates/${templateId}/sample`, authHeaders());
    if (!r.data?.success) return null;
    return { sample: !!r.data.sample, turns: (r.data.turns || []).filter((t) => t && t.text).map((t) => ({ role: t.role === "patient" ? "patient" : "agent", text: String(t.text) })) };
  } catch (_) { return null; }
}

/* ---------- Analytics (items 216–218) ---------- */

/**
 * GET /api/ai-agent/analytics?period=7d — totals for the location when facilityId is given
 * (conversations of agents assigned to it, bookings made at it), and per agent over 7 days.
 * → { conversations, bookings, perAgent: Map(config_id → { conversations, bookings }) } or null.
 */
export async function loadAgentAnalytics(facilityId = null) {
  try {
    const r = await axios.get(`${config.API_BASE_URL}/api/ai-agent/analytics`, { ...authHeaders(), params: { period: "7d", ...(facilityId ? { facility_id: facilityId } : {}) } });
    const a = r.data?.analytics;
    if (!r.data?.success || !a) return null;
    const total = a.conversations?.total_conversations;
    return {
      conversations: total == null ? null : Number(total),
      bookings: a.bookings == null ? null : Number(a.bookings),
      perAgent: new Map((a.per_agent || []).map((row) => [String(row.config_id), { conversations: Number(row.conversations_7d) || 0, bookings: Number(row.bookings_7d) || 0 }])),
    };
  } catch (_) { return null; }
}

/* ---------- Template actions (enabled_tools) ---------- */

const TOOL_LABEL = {
  "kb.search": "Answer from knowledge base",
  "appointments.book": "Book",
  "appointments.cancel": "Cancel",
  "appointments.reschedule": "Reschedule",
  "appointments.check_slots": "Check slots",
  "appointments.list_for_patient": "List appointments",
  "cases.register_complaint": "Register complaint",
  "cases.submit_feedback": "Submit feedback",
  "cases.list_for_patient": "List complaints",
  "cases.list_feedback_for_patient": "List feedback",
  "branches.find_nearest": "Find nearest branch",
  "facilities.find_nearest": "Find nearest branch",
  "facility.info": "Clinic information",
  "doctors.search": "Find doctor",
  "patient.lookup": "Find patient",
  "patient.register": "Register patient",
  "triage.assess": "Triage",
};
const humanize = (tool) => {
  const last = String(tool).split(".").pop().replace(/_/g, " ");
  return last.charAt(0).toUpperCase() + last.slice(1);
};
/** Labels for a template's whitelisted tools. null when the template whitelists nothing (= every tool). */
export const templateActions = (template) => {
  const tools = parseJson(template?.enabled_tools, null);
  if (!Array.isArray(tools)) return null;
  return [...new Set(tools.map((t) => TOOL_LABEL[t] || humanize(t)))];
};

/* ---------- Notifications (old page helpers, unchanged) ---------- */

const ACTION_TOOLS = [
  "book_appointment", "cancel_appointment", "reschedule_appointment", "register_complaint", "submit_feedback",
  "appointments.book", "appointments.cancel", "appointments.reschedule", "cases.register_complaint", "cases.submit_feedback",
];
export const templateActionTools = (template) => {
  if (!template) return [];
  let tools = template.enabled_tools;
  if (tools === null || tools === undefined) return [...ACTION_TOOLS];
  tools = parseJson(tools, null);
  if (!Array.isArray(tools)) return [];
  return tools.filter((t) => ACTION_TOOLS.includes(t));
};
export const eventRunsOnTemplate = (meta, enabledActionTools) => {
  const candidates = Array.isArray(meta.source_tools) && meta.source_tools.length ? meta.source_tools : [meta.source_tool];
  return candidates.some((t) => enabledActionTools.includes(t));
};
const templateIsWhatsAppOnly = (template) => {
  const channels = enabledChannels(template?.channel_config);
  return channels.length > 0 && channels.every((c) => c === "whatsapp");
};
export const parseNotificationConfig = (raw) => parseJson(raw, null) || { whatsapp_enabled: true, events: {} };

/** Which wizard steps a template needs — same rules as the old wizard, plus the design's widget and review steps. */
export function stepKeysFor(template) {
  const channels = enabledChannels(template?.channel_config);
  const keys = ["template", "identity"];
  if (channels.includes("web_widget")) keys.push("widget");
  if (template && templateActionTools(template).length > 0 && !templateIsWhatsAppOnly(template)) keys.push("notifications");
  if (channels.includes("web_voice") || channels.includes("voice_call")) keys.push("languages");
  keys.push("review");
  return keys;
}
export const STEP_LABEL = { template: "Template", identity: "Identity", widget: "Widget design", notifications: "Notifications", languages: "Languages", review: "Review & deploy" };

/* ---------- Languages ---------- */

export const LANGS = [
  ["hi-IN", "Hindi"], ["en-IN", "English (India)"], ["bn-IN", "Bengali"], ["ta-IN", "Tamil"], ["te-IN", "Telugu"], ["mr-IN", "Marathi"],
  ["gu-IN", "Gujarati"], ["kn-IN", "Kannada"], ["ml-IN", "Malayalam"], ["or-IN", "Odia"], ["as-IN", "Assamese"], ["ne-NP", "Nepali"],
  ["en-US", "English (US)"], ["en-GB", "English (UK)"], ["en-AU", "English (Australia)"], ["ar-XA", "Arabic"], ["de-DE", "German"],
  ["es-ES", "Spanish (Spain)"], ["es-US", "Spanish (US)"], ["fr-FR", "French"], ["fr-CA", "French (Canada)"], ["it-IT", "Italian"],
  ["ja-JP", "Japanese"], ["ko-KR", "Korean"], ["nl-NL", "Dutch"], ["pl-PL", "Polish"], ["pt-BR", "Portuguese (Brazil)"], ["ru-RU", "Russian"],
  ["sv-SE", "Swedish"], ["tr-TR", "Turkish"], ["vi-VN", "Vietnamese"], ["th-TH", "Thai"], ["id-ID", "Indonesian"], ["ms-MY", "Malay"],
];
export const langLabel = (code) => (LANGS.find(([c]) => c === code) || [code, code])[1];
/** One Indian language per agent; English (India) rides along (old page: isIndic). */
export const isIndic = (code) => /-IN$/i.test(code) && !/^en-/i.test(code);
export const LANG_CAP = 2;

/* ---------- Agent rows ---------- */

export const GENDER_LABEL = { neutral: "Neutral", female: "Female", male: "Male" };
export const GENDER_KEY = { Neutral: "neutral", Female: "female", Male: "male" };
export const ORG_SCOPE = "Organization-wide (all facilities)";
export const FACILITY_SCOPE = "Specific facilities";

/** Wizard state for a new agent (old page: openWizard() defaults). */
export const emptyWizard = () => ({
  template_id: null,
  agent_name: "",
  agent_gender: "neutral",
  greeting_message: "",
  timezone: "Asia/Kolkata",
  tts_voice: "",
  tts_speed: 1.0,
  waba_config_id: null,
  facility_scope: "org",
  facility_id: null,
  ask_patient_location: false,
  triage_enabled: false,
  temperature: null,
  supported_language_codes: ["hi-IN", "en-IN"],
  default_language_code: "hi-IN",
  channel_language_overrides: {},
  restricted_specialties: [],
  notification_config: { whatsapp_enabled: true, events: {} },
  // A pool number to attach at deploy (item 222); only sent when creating.
  phone_number_id: null,
});

/** Wizard state for editing a saved agent (old page: openWizard(config)). */
export const wizardFromConfig = (c) => ({
  template_id: c.template_id,
  agent_name: c.agent_name || "",
  agent_gender: c.agent_gender || "neutral",
  greeting_message: c.greeting_message || "",
  timezone: c.timezone || "Asia/Kolkata",
  tts_voice: c.tts_voice || "Puck",
  tts_speed: c.tts_speed || 1.0,
  waba_config_id: c.waba_config_id || null,
  facility_scope: c.facility_scope || "org",
  facility_id: c.facility_id || null,
  ask_patient_location: c.ask_patient_location || false,
  triage_enabled: !!c.triage_enabled,
  temperature: c.temperature !== undefined && c.temperature !== null ? parseFloat(c.temperature) : null,
  supported_language_codes: parseJson(c.supported_language_codes, null) || ["hi-IN", "en-IN"],
  default_language_code: c.default_language_code || (parseJson(c.supported_language_codes, [])[0]) || "hi-IN",
  channel_language_overrides: {},
  restricted_specialties: parseJson(c.restricted_specialties, []) || [],
  notification_config: parseNotificationConfig(c.notification_config),
  phone_number_id: null,
});

/** Old page: buildChannelOverridesPayload(). */
export const channelOverridesPayload = (map = {}) => Object.keys(map).flatMap((channel) => {
  const o = map[channel];
  if (!o || !Array.isArray(o.codes) || o.codes.length === 0) return [];
  return [{ channel, supported_language_codes: o.codes, default_language_code: o.default || null }];
});

/** Live / Paused from is_active. The backend has no draft agents. */
export const agentStatus = (c) => (c.is_active ? "Live" : "Paused");

/** "WhatsApp · +91…", "Phone · +91…" from what the backend links to an agent. */
export function agentConnections(c, phones = []) {
  const out = [];
  if (c.waba_config_id && c.waba_phone_number) out.push(`WhatsApp · ${c.waba_display_name || c.waba_phone_number}`);
  phones.filter((p) => String(p.agent_config_id) === String(c.id)).forEach((p) => out.push(`Phone · ${p.phone_number}`));
  return out;
}

/* ---------- Widget design (widget_config ↔ design shape) ---------- */

export const DEFAULT_WIDGET_DESIGN = {
  primaryColor: "#ddfd2c", position: "Bottom right", headerAvatar: "", headerTitle: "Chat with us",
  headerSubtitle: "We typically reply within minutes", welcomeMessage: "", buttonIcon: "", buttonSize: 60, borderRadius: 16,
  domain: "",
};
export function widgetFromConfig(raw) {
  const w = parseJson(raw, {}) || {};
  return {
    primaryColor: w.primary_color || DEFAULT_WIDGET_DESIGN.primaryColor,
    position: w.position === "bottom-left" ? "Bottom left" : "Bottom right",
    headerAvatar: w.header_avatar || "",
    headerTitle: w.title ?? DEFAULT_WIDGET_DESIGN.headerTitle,
    headerSubtitle: w.subtitle ?? DEFAULT_WIDGET_DESIGN.headerSubtitle,
    welcomeMessage: w.welcome_message || "",
    buttonIcon: w.button_icon || "",
    buttonSize: Number(w.button_size) || DEFAULT_WIDGET_DESIGN.buttonSize,
    borderRadius: w.border_radius != null ? Number(w.border_radius) : DEFAULT_WIDGET_DESIGN.borderRadius,
    domain: w.domain || "",
  };
}
/** Keeps fields the design does not edit (show_branding, …) from the stored config. */
export const widgetToConfig = (d, raw) => ({
  ...(parseJson(raw, {}) || {}),
  primary_color: d.primaryColor,
  position: d.position === "Bottom left" ? "bottom-left" : "bottom-right",
  title: d.headerTitle,
  subtitle: d.headerSubtitle,
  welcome_message: d.welcomeMessage,
  button_size: d.buttonSize,
  border_radius: d.borderRadius,
  header_avatar: d.headerAvatar,
  button_icon: d.buttonIcon,
  // The site the widget is embedded on (item 223); the backend keeps the bare host.
  domain: String(d.domain || "").trim() || null,
});

/* ---------- Clinic name ---------- */

let orgNamePromise = null;
/** The signed-in organisation's name (GET /api/organizations/my-organizations), cached. */
export function loadOrgName() {
  if (!orgNamePromise) {
    orgNamePromise = organizationsAPI.getMyOrganizations().then((r) => {
      const user = (() => { try { return JSON.parse(localStorage.getItem("user") || "{}") || {}; } catch (_) { return {}; } })();
      const orgs = r.data?.success ? r.data.data || [] : [];
      return (orgs.find((o) => o.id === user.organization_id) || orgs[0])?.name || "";
    }).catch(() => { orgNamePromise = null; return ""; });
  }
  return orgNamePromise;
}

export const errorMessage = (err, fallback) => err?.response?.data?.message || fallback;
